import axios from 'axios';
import store from '@/store';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Mutations, Actions, Endpoints, GlobalOverlays } from '@/ts/store/Constants';
import ErrorResponse from '@/ts/ErrorResponse';
import type { TicketMessage } from '@/ts/interfaces/Tickets';
import { STAFF_TICKETS_TABS } from '@/ts/interfaces/Tickets';
import cloneDeep from 'lodash/cloneDeep';
import sortBy from 'lodash/sortBy';
import orderBy from 'lodash/orderBy';

@Module({name: 'UserTickets', namespaced: true})
export default class UserTickets extends VuexModule {
    public userTickets: any[] = [];
    public socketInitTicketIds: any = [];
    public userTicketAllTypes: any[] = [];
    public userTicketAllInquiries: any[] = [];
    public userTicketAllCategories: any[] = [];
    public userTicketCurrentType: any = [];
    public userCurrentTicket: any = [];
    public userTicketConversation: any = [];
    public userTicketCurrentInquiry: any = [];
    public userTicketCurrentCategory: any = [];
    public previewTicketVisibility: boolean = false;
    public addNewTicketTypeVisibility: boolean = false;
    public addNewTicketThanksVisibility: boolean = false;
    public addNewTicketThanksMode: string = 'NotNewTicket';
    public addNewTicketAnswerVisibility: boolean = false;
    public addNewTicketInquiryVisibility: boolean = false;
    public addNewTicketCategoryVisibility: boolean = false;
    public ticketAttachmentsOverlayVisibility: boolean = false;
    public previewTicketComversationVisibility: boolean = false;
    public ticketCreateFormVisibility: boolean = false;
    public selectedTicketPriority: number = -1;
    public ticketSearchQuery: string = '';
    public ticketPriorityDropdownVisibility: boolean = false;
    public ticketRemainWatcherVisibility: boolean = false;
    public activeTicketImages: any = [];
    public activeStaffTicketsType: string = STAFF_TICKETS_TABS.MY_TICKETS;
    public activeStaffTicketsOrder: string = 'NEWEST';

    @Mutation
    public RESET_USER_TICKETS_STORE_STATE() {
        this.userTickets                         = [];
        this.socketInitTicketIds                 = [];
        this.userTicketAllTypes                  = [];
        this.userTicketAllInquiries              = [];
        this.userTicketAllCategories             = [];
        this.userTicketCurrentType               = [];
        this.userCurrentTicket                   = [];
        this.userTicketConversation              = [];
        this.userTicketCurrentInquiry            = [];
        this.userTicketCurrentCategory           = [];
        this.previewTicketVisibility             = false;
        this.addNewTicketTypeVisibility          = false;
        this.addNewTicketThanksVisibility        = false;
        this.addNewTicketThanksMode              = 'NotNewTicket';
        this.addNewTicketAnswerVisibility        = false;
        this.addNewTicketInquiryVisibility       = false;
        this.addNewTicketCategoryVisibility      = false;
        this.ticketAttachmentsOverlayVisibility  = false;
        this.previewTicketComversationVisibility = false;
        this.ticketCreateFormVisibility          = false;
        this.selectedTicketPriority              = -1;
        this.ticketSearchQuery                   = '';
        this.ticketPriorityDropdownVisibility    = false;
        this.ticketRemainWatcherVisibility       = false;
        this.activeTicketImages                  = [];
        this.activeStaffTicketsType              = STAFF_TICKETS_TABS.MY_TICKETS;
        this.activeStaffTicketsOrder             = 'NEWEST';
    }

    get getSelectedTicketPriority() {
        return this.selectedTicketPriority;
    }

    get getTicketSearchQuery() {
        return this.ticketSearchQuery;
    }

    get getTicketAttachmentsOverlayVisibility() {
        return this.ticketAttachmentsOverlayVisibility;
    }

    get getAllUserTickets() {
        return this.userTickets;
    }

    get getAllUserTicketsOrdered() {
        const tickets = cloneDeep(this.userTickets);

        switch (this.activeStaffTicketsOrder) {
            case 'OLDEST':
                return sortBy(tickets, (ticket) => ticket.id);
            case 'PROJECT':
                return sortBy(tickets, (ticket) => ticket.project);
            case 'PRIORITY_CRITICAL':
                return orderBy(tickets, 'priority', 'desc');
            case 'PRIORITY_LOW':
                return sortBy(tickets, (ticket) => ticket.priority);
            case 'STATUS_PENDING':
                return sortBy(tickets, (ticket) => ticket.state_id);
            case 'STATUS_INPROGRESS':
                return orderBy(tickets, 'state_id', 'desc');
            default:
                return tickets;
        }
    }

    get getUserCurrentTicket() {
        return this.userCurrentTicket;
    }

    get getUserTicketAllTypes() {
        return this.userTicketAllTypes;
    }

    get getUserTicketConversation() {
        return this.userTicketConversation;
    }

    get getUserTicketAllInquiries() {
        return this.userTicketAllInquiries;
    }

    get getUserTicketAllCategories() {
        return this.userTicketAllCategories;
    }

    get getUserTicketCurrentType() {
        return this.userTicketCurrentType;
    }

    get getUserTicketCurrentCategory() {
        return this.userTicketCurrentCategory;
    }

    get getUserTicketCurrentInquiry() {
        return this.userTicketCurrentInquiry;
    }

    get getPreviewTicketVisibility() {
        return this.previewTicketVisibility;
    }

    get getAddNewTicketTypeVisibility() {
        return this.addNewTicketTypeVisibility;
    }

    get getAddNewTicketCategoryVisibility() {
        return this.addNewTicketCategoryVisibility;
    }

    get getPreviewTicketComversationVisibility() {
        return this.previewTicketComversationVisibility;
    }

    get getAddNewTicketThanksVisibility() {
        return this.addNewTicketThanksVisibility;
    }

    get getAddNewTicketThanksMode() {
        return this.addNewTicketThanksMode;
    }

    get getAddNewTicketInquiryVisibility() {
        return this.addNewTicketInquiryVisibility;
    }

    get getAddNewTicketAnswerVisibility() {
        return this.addNewTicketAnswerVisibility;
    }

    get getUserTicket() {
        return (ticketId: number) => {
            return this.userTickets.find((ticket: any) => ticket.id === ticketId);
        };
    }

    get getTicketPriorityDropdownVisibility() {
        return this.ticketPriorityDropdownVisibility;
    }

    get getRicketRemainWatcherVisibility() {
        return this.ticketRemainWatcherVisibility;
    }

    get getTicketCreateFormVisibility(): boolean {
        return this.ticketCreateFormVisibility;
    }

    get getSocketInitTicketIds() {
        return this.socketInitTicketIds;
    }

    get getActiveTicketImages() {
        return this.activeTicketImages;
    }

    get getActiveStaffTicketsOrder(): string {
        return this.activeStaffTicketsOrder;
    }

    get getActiveStaffTicketsType(): string {
        return this.activeStaffTicketsType;
    }

    @Action
    public FETCH_ALL_USER_TICKETS(data: any) {
        axios.get(Endpoints.Tickets.ALL_USER_TICKETS(data.type))
            .then((response: any) => {
                store.commit(Mutations.SET_ALL_USER_TICKETS, response.data);
                store.commit(Mutations.SET_ACTIVE_STAFF_TICKETS_TYPE, data.type);
                for (const ticket of response.data) {
                    if (store.getters['Socket/isSocketActive'] && data.vue && !store.getters['UserTickets/getSocketInitTicketIds'].includes(ticket.id)) {
                        store.dispatch(Actions.INITIALIZE_USER_TICKET_SOCKET, {vue: data.vue, ticketId: ticket.id});
                        store.commit(Mutations.ADD_SOCKET_INIT_USER_TICKET_ID, ticket.id);
                    }
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_USER_TICKET_ALL_TYPES() {
        axios.get(Endpoints.Tickets.USER_TICKET_ALL_TYPES)
            .then((response: any) => {
                store.commit(Mutations.SET_USER_TICKET_ALL_TYPES, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_USER_TICKET_ALL_CATEGORIES(typeId: number) {
        axios.get(Endpoints.Tickets.USER_TICKET_ALL_CATEGORIES(typeId))
            .then((response: any) => {
                store.commit(Mutations.SET_USER_TICKET_ALL_CATEGORIES, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_USER_TICKET_ALL_INQUIRIES(data: any) {
        axios.get(Endpoints.Tickets.USER_TICKET_ALL_INQUIRIES(data.typeId, data.categoryId))
            .then((response: any) => {
                store.commit(Mutations.SET_USER_TICKET_ALL_INQUIRIES, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_USER_CURRENT_TICKET_TYPE(typeId: number) {
        axios.get(Endpoints.Tickets.USER_CURRENT_TICKET_TYPE(typeId))
            .then((response: any) => {
                store.commit(Mutations.SET_USER_CURRENT_TICKET_TYPE, response.data);
                store.dispatch(Actions.FETCH_USER_TICKET_ALL_CATEGORIES, response.data.id);

                store.commit(Mutations.SET_GLOBAL_OVERLAY_VISIBILITY, true);
                store.commit(Mutations.SET_GLOBAL_OVERLAY_COMPONENT, GlobalOverlays.TICKET_CATEGORY);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_USER_CURRENT_TICKET_CATEGORY(categoryId: number) {
        axios.get(Endpoints.Tickets.USER_CURRENT_TICKET_CATEGORY(categoryId))
            .then((response: any) => {
                store.commit(Mutations.SET_USER_CURRENT_TICKET_CATEGORY, response.data);
                store.dispatch(Actions.FETCH_USER_TICKET_ALL_INQUIRIES, { typeId: store.getters['UserTickets/getUserTicketCurrentType'].id , categoryId});
                store.commit(Mutations.SET_GLOBAL_OVERLAY_COMPONENT, GlobalOverlays.TICKET_INQUIRY);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_USER_CURRENT_TICKET_INQUIRY(inquiryId: number) {
        axios.get(Endpoints.Tickets.USER_CURRENT_TICKET_INQUIRY(inquiryId))
            .then((response: any) => {
                store.commit(Mutations.SET_USER_CURRENT_TICKET_INQUIRY, response.data);
                store.commit(Mutations.SET_ADD_NEW_TICKET_INQUIRY_VISIBILITY, false);
                store.commit(Mutations.SET_ADD_NEW_TICKET_ANSWER_VISIBILITY, true);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_USER_CURRENT_TICKET(data: any) {
        const endpoint = data.admin
            ? Endpoints.Admin.TicketSystem.CURRENT_TICKET(data.id)
            : Endpoints.Tickets.USER_CURRENT_TICKET(data.id);

        return axios.get(endpoint)
            .then((response: any) => {
                store.commit(Mutations.SET_USER_CURRENT_TICKET, data.admin ? response.data.data : response.data);

                if (data.admin) {
                    const ticket = response.data.data;

                    store.dispatch(Actions.FETCH_TICKET_IMAGES, ticket.id);
                    if (!ticket.firstResponse) {
                        store.dispatch(Actions.USER_TICKET_RESPONDED, ticket.id);
                    }
                } else {
                    if (!data.chat) {
                        store.commit(Mutations.SET_PREVIEW_TICKET_VISIBILITY, true);
                    } else {
                        store.commit(Mutations.SET_PREVIEW_TICKET_CONVERSATION_VISIBILITY, true);
                    }
                }


            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_USER_TICKET_CONVERSATION(conversationId: number) {
        axios.get(Endpoints.Tickets.USER_TICKET_CONVERSATION(conversationId))
            .then((response: any) => {
                store.commit(Mutations.SET_USER_TICKET_CONVERSATION, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public USER_ADD_NEW_TICKET(data: any) {
        axios.post(Endpoints.Tickets.USER_ADD_NEW_TICKET, data.requestData)
            .then((response) => {
                store.commit(Mutations.ADD_NEW_USER_TICKET, response.data);

                if (store.getters['Socket/isSocketActive'] && store.getters['Socket/isTicketSocketInitialized']) {
                    store.dispatch(Actions.INITIALIZE_USER_TICKET_SOCKET, {vue: data.vue, ticketId: response.data.id});
                    store.commit(Mutations.ADD_SOCKET_INIT_USER_TICKET_ID, response.data.id);
                }

                return data.callback(response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    console.log(error.response.data);
                } else {
                    console.log(error);
                }

                return data.callback(null);
            });
    }

    @Action
    public REMOVE_TICKET_IMAGE(data: any) {
        axios.delete(Endpoints.Tickets.REMOVE_TICKET_IMAGE(data.ticketId, data.imageId))
            .then((response) => {

            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    console.log(error.response.data);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public SEND_TICKET_MESSAGE_TO_ADMIN(requestData: TicketMessage) {
        return axios.post(Endpoints.Tickets.TICKET_MESSAGE_TO_ADMIN, requestData)
            .then((response) => {
                if (!store.getters['Socket/isSocketActive'] && !store.getters['Socket/isTicketSocketInitialized']) {
                    store.commit(Mutations.PUSH_TO_USER_TICKET_CONVERSATION, response.data);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    console.log(error.response.data);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public USER_CANCEL_TICKET(ticketId: number) {
        axios.delete(Endpoints.Tickets.USER_CANCEL_TICKET(ticketId))
            .then(() => {
                store.dispatch(Actions.FETCH_ALL_USER_TICKETS, { vue: null, type: 'all' });
                store.commit(Mutations.SET_PREVIEW_TICKET_VISIBILITY, false);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public CHANGE_TICKET_STATE(ticket: any) {
        axios.patch(Endpoints.Tickets.USER_EDIT_TICKET_STATE(ticket.id), {state: ticket.state_id})
            .then((response) => {
                if (!store.getters['Socket/isSocketActive'] && !store.getters['Socket/isTicketSocketInitialized']) {
                    store.commit(Mutations.UPDATE_SELECTED_TICKET_STATE, response);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public CHANGE_TICKET_ASSIGNEE(data: any) {
        axios.patch(Endpoints.Tickets.USER_EDIT_TICKET_ASSIGNEE(data.ticketId), {assignee: data.assigneeId, remain: data.remain})
            .then((response) => {
                store.commit(Mutations.TICKET_REMAIN_WATCHER_VISIBILITY, false);
                if (!store.getters['Socket/isSocketActive'] && !store.getters['Socket/isTicketSocketInitialized']) {
                    store.commit(Mutations.UPDATE_SELECTED_TICKET_ASSIGNEE, response);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public INITIALIZE_USER_TICKET_SOCKET(data: any) {
        const ticketChannel = 'ticket.' + data.ticketId;

        data.vue.$echo.private(ticketChannel)
            .listen(('.ticket.updated'), (ticketPayload: any) => {
                store.commit(Mutations.EXECUTE_USER_TICKET_UPDATE, ticketPayload);
            });

        store.commit(Mutations.TICKET_SOCKET_INITIALIZED);
    }

    @Action
    public CHANGE_TICKET_PRIORITY(data: any) {
        axios.patch(Endpoints.Admin.TicketSystem.TICKET_PRIORITY(data.ticketId), {priorityId: data.priorityId})
            .then((response) => {
                if (!store.getters['Socket/isSocketActive'] && !store.getters['Socket/isTicketSocketInitialized']) {
                    store.commit(Mutations.SET_USER_TICKET_PRIORITY, response);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public USER_TICKET_RESPONDED(ticketId: number) {
        axios.post(Endpoints.Tickets.USER_TICKET_RESPONDED(ticketId))
            .then((response) => {
                store.commit(Mutations.MAKE_USER_TICKET_RESPONDED, {data: response.data, ticketId});
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_TICKET_IMAGES(ticketId: number) {
        axios.post(Endpoints.Tickets.TICKET_IMAGES(ticketId))
            .then((response) => {
                store.commit(Mutations.SET_ACTIVE_TICKET_IMAGES, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Mutation
    public ADD_NEW_USER_TICKET(ticket: any) {
        this.userTickets.unshift(ticket);
    }

    @Mutation
    public UPDATE_TICKET_UNREAD_MESSAGES(ticketPayload: any) {
        const ticket = this.userTickets.find((t: any) => t.id === ticketPayload.id);
        ticket.conversation.messages = ticketPayload.conversation.messages;
    }

    @Mutation
    public SET_ALL_USER_TICKETS(tickets: any) {
        this.userTickets = tickets;
    }

    @Mutation
    public SET_ADD_NEW_TICKET_TYPE_VISIBILITY(visibility: boolean) {
        this.addNewTicketTypeVisibility = visibility;
    }

    @Mutation
    public SET_ADD_NEW_TICKET_CATEGORY_VISIBILITY(visibility: boolean) {
        this.addNewTicketCategoryVisibility = visibility;
    }

    @Mutation
    public SET_ADD_NEW_TICKET_INQUIRY_VISIBILITY(visibility: boolean) {
        this.addNewTicketInquiryVisibility = visibility;
    }

    @Mutation
    public SET_ADD_NEW_TICKET_ANSWER_VISIBILITY(visibility: boolean) {
        this.addNewTicketAnswerVisibility = visibility;
    }

    @Mutation
    public SET_ADD_NEW_TICKET_THANKS_VISIBILITY(visibility: boolean) {
        this.addNewTicketThanksVisibility = visibility;
    }

    @Mutation
    public SET_ADD_NEW_TICKET_THANKS_MODE(mode: string) {
        this.addNewTicketThanksMode = mode;
    }

    @Mutation
    public SET_PREVIEW_TICKET_VISIBILITY(visibility: boolean) {
        this.previewTicketVisibility = visibility;
    }

    @Mutation
    public SET_PREVIEW_TICKET_CONVERSATION_VISIBILITY(visibility: boolean) {
        this.previewTicketComversationVisibility = visibility;
    }

    @Mutation
    public SET_USER_CURRENT_TICKET(ticket: any) {
        this.userCurrentTicket = ticket;
    }

    @Mutation
    public SET_USER_TICKET_ALL_TYPES(data: any) {
        this.userTicketAllTypes = data;
    }

    @Mutation
    public SET_USER_TICKET_ALL_INQUIRIES(data: any) {
        this.userTicketAllInquiries = data;
    }

    @Mutation
    public SET_USER_TICKET_ALL_CATEGORIES(data: any) {
        this.userTicketAllCategories = data;
    }

    @Mutation
    public SET_USER_CURRENT_TICKET_TYPE(data: any) {
        this.userTicketCurrentType = data;
    }

    @Mutation
    public SET_USER_CURRENT_TICKET_CATEGORY(data: any) {
        this.userTicketCurrentCategory = data;
    }

    @Mutation
    public SET_USER_CURRENT_TICKET_INQUIRY(data: any) {
        this.userTicketCurrentInquiry = data;
    }

    @Mutation
    public MAKE_USER_TICKET_RESPONDED(data: any) {
        const ticket = this.userTickets.find((t: any) => t.id === data.ticketId);
        if (!ticket.firstResponse) {
            ticket.firstResponse = data.data.firstResponse;
            ticket.firstResponder = data.data.firstResponder;
        }
    }

    @Mutation
    public SET_USER_TICKET_CONVERSATION(data: any) {
        this.userTicketConversation = data;

        setTimeout(() => {
            const objDiv = document.getElementById('user-communication-msg-block-id');
            if (objDiv) {
                objDiv.scrollTop = objDiv.scrollHeight;
            }
        }, 500);
    }

    @Mutation
    public PUSH_TO_USER_TICKET_CONVERSATION(data: any) {
        if (data.action !== null && store.state.User.personData.isTenant) {
            return;
        }

        if (typeof this.userTicketConversation.messages !== 'undefined') {
            if (this.userTicketConversation.id === data.conversation_id) {
                this.userTicketConversation.messages.push(data);
            }

            setTimeout(() => {
                const objDiv = document.getElementById('user-communication-msg-block-id');
                objDiv.scrollTop = objDiv.scrollHeight;
            }, 200);
        }

        const ticket = this.userTickets.find((tk: any) => tk.conversation && tk.conversation.id === data.conversation_id);

        if (ticket) {
            const checkForMessage = ticket.conversation.messages.find((message: any) => message.id === data.id);

            if (typeof checkForMessage === 'undefined') {
                ticket.conversation.messages.push(data);
            }
        }
    }

    @Mutation
    public REMOVE_USER_TICKET_CONVERSATION_MESSAGE(data: any) {
        if (typeof this.userTicketConversation.messages !== 'undefined') {
            if (this.userTicketConversation.id === data.conversation_id) {
                const message = this.userTicketConversation.messages.find((m: any) => m.id === data.id);

                if (message) {
                    this.userTicketConversation.messages = this.userTicketConversation.messages.filter((m: any) => m.id !== data.id);
                }
            }
        }
    }

    @Mutation
    public SET_TICKET_SELECTED_PRIORITY(status: number) {
        this.selectedTicketPriority = status;
    }

    @Mutation
    public SET_TICKET_SEARCH_QUERY(query: string) {
        this.ticketSearchQuery = query;
    }

    @Mutation
    public SET_PREVIEW_TICKET_ATTACHMENTS_VISIBILITY(visibility: boolean) {
        this.ticketAttachmentsOverlayVisibility = visibility;
    }

    @Mutation
    public UPDATE_SELECTED_TICKET_STATE(data: any) {
        if (this.userCurrentTicket && this.userCurrentTicket.id === data.ticketId) {
            this.userCurrentTicket.state_id = data.data.state_id;
            this.userCurrentTicket.state    = data.data.state;
        }

        const ticket = this.userTickets.find((tk: any) => tk.id === data.ticketId);
        if (ticket) {
            ticket.state_id = data.data.state_id;
            ticket.state    = data.data.state;
        }
    }

    @Mutation
    public UPDATE_SELECTED_TICKET_ASSIGNEE(data: any) {
        if (this.userCurrentTicket && this.userCurrentTicket.id === data.ticketId) {
            this.userCurrentTicket.assigne_id    = data.data.assigne_id;
            this.userCurrentTicket.assignee_name = data.data.assignee_name;
        }

        const ticket = this.userTickets.find((tk: any) => tk.id === data.ticketId);
        if (ticket) {
            ticket.assigne_id    = data.data.assigne_id;
            ticket.assignee_name = data.data.assignee_name;
        }
    }

    @Mutation
    public SET_USER_TICKET_PRIORITY(data: any) {
        if (this.userCurrentTicket && this.userCurrentTicket.id === data.ticketId) {
            this.userCurrentTicket.priority = data.data.priority;
        }

        const ticket = this.userTickets.find((tk: any) => tk.id === data.ticketId);
        if (ticket) {
            ticket.priority = data.data.priority;
        }
    }

    @Mutation
    public RESET_TICKET_UNREAD_COUNT(ticketId: number) {
        const ticket = this.userTickets.find((t: any) => t.id === ticketId);
        ticket.conversation.messages = [];
    }

    @Mutation
    public TICKET_PRIORITY_DROPDOWN_VISIBILITY(visibility: boolean) {
        this.ticketPriorityDropdownVisibility = visibility;
    }

    @Mutation
    public TICKET_REMAIN_WATCHER_VISIBILITY(visibility: boolean) {
        this.ticketRemainWatcherVisibility = visibility;
    }

    @Mutation
    public SET_TICKET_CREATE_FORM_OVERLAY_VISIBILITY(visibility: boolean) {
        this.ticketCreateFormVisibility = visibility;
    }

    @Mutation
    public ADD_SOCKET_INIT_USER_TICKET_ID(id: number) {
        this.socketInitTicketIds.push(id);
    }

    @Mutation
    public EXECUTE_USER_TICKET_UPDATE(data: any) {
        if (data.type === 'message') {
            store.commit(Mutations.PUSH_TO_USER_TICKET_CONVERSATION, data.data);
        } else if (data.type === 'state') {
            store.commit(Mutations.UPDATE_SELECTED_TICKET_STATE, data.data);
        } else if (data.type === 'assignee') {
            store.commit(Mutations.UPDATE_SELECTED_TICKET_ASSIGNEE, data.data);
        } else if (data.type === 'priority') {
            store.commit(Mutations.SET_USER_TICKET_PRIORITY, data.data);
        } else if (data.type === 'message_deleted') {
            store.commit(Mutations.REMOVE_TICKET_CONVERSATION_MESSAGE, data.data);
            store.commit(Mutations.REMOVE_USER_TICKET_CONVERSATION_MESSAGE, data.data);
        }
    }

    @Mutation
    public SET_ACTIVE_TICKET_IMAGES(images: any) {
        this.activeTicketImages = images;
    }

    @Mutation
    public SET_ACTIVE_STAFF_TICKETS_ORDER(order: string) {
        this.activeStaffTicketsOrder = order;
    }

    @Mutation
    public SET_ACTIVE_STAFF_TICKETS_TYPE(type: string) {
        this.activeStaffTicketsType = type;
    }
}
